// src/modules/app-views/goodsReceipt/pages/Init/index.jsx
// *******************************************************************************
// GOODS RECEIPT LIST PAGE
// *******************************************************************************
// Main listing page for goods receipt (NF-e entry documents).
// Displays a table with all received goods and allows filtering by status and supplier.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useCallback, useRef, useState } from 'react';
import { Table, Tooltip, Button, Space, Dropdown } from 'antd';
import {
  FileTextOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  ContainerOutlined,
  RestOutlined,
} from '@ant-design/icons';
import { PiLifebuoy } from 'react-icons/pi';
import { useListContext } from 'components/core/ListProvider';
import { useModuleContext } from 'components/core/ModuleProvider';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { isMobileView } from 'utils/helpers';
import {
  documentTypeColumn,
  documentNumberColumn,
  orderNumberColumn,
  operationNatureColumn,
  issueDateColumn,
  itemsCountColumn,
  totalAmountColumn,
} from 'modules/app-views/invoices/pages/Init/components/tableColumns';
import { statusColumn, operationTypeColumn, issuerColumn } from './components/tableColumns';
import {
  itemLineNumberColumn,
  itemSkuColumn,
  itemDescriptionColumn,
  itemUnitColumn,
  itemQuantityColumn,
  itemUnitPriceColumn,
  itemTotalColumn,
  itemNcmColumn,
  itemCfopColumn,
} from 'modules/app-views/invoices/pages/Init/components/tableColumns/items';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const GoodsReceiptInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    data,
    loading,
    tableKey,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildCreatedAtColumn,
    buildUpdatedAtColumn,
    buildDocumentInfoIconsColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    handleTableChanges,
    pagination,
    rowSelection,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    query,
    TableFeatures,
    refresh,
    registerFloatButtonDisabledActions,
  } = useListContext();

  const {
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    handlePreviewDocument,
  } = useModuleContext();

  const storedLanguage = useZustand('theme', 'language');
  const storedListSettings = useZustand('module', 'listSettings');

  // Current folder
  const folder = storedListSettings?.filterDrawer?.folder || query?.drawer?.folder || 'inbox';

  // Serialize sort, search, filter, and pagination for stable dependencies
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});
  const filterKey = JSON.stringify(query?.filter || {});
  const paginationKey = pagination?.current || 1;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // Track expanded rows (subtables) - controlled to reset on sort/filter/search/pagination
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. REFS & HANDLERS (using refs to maintain stable function identity)
  // ─────────────────────────────────────────────────────────────────────────────

  // Store handlers in refs to avoid re-creating render functions
  const handlersRef = useRef({});
  handlersRef.current = {
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    handlePreviewDocument,
    refresh,
    folder,
  };

  // Stable render function for DOCUMENTS/INBOX folder (doesn't change identity)
  const renderDocumentsActions = useCallback((_, record) => {
    const {
      handleEditDocument,
      handleArchiveDocuments,
      handleRemoveDocuments,
      handlePreviewDocument,
      refresh,
    } = handlersRef.current;

    const status = record?.status;
    const isDraft = status === 'draft';

    const actionOptions = { onSuccess: refresh };

    // Only draft status allows archive/remove
    const canModify = isDraft;

    const allButtons = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: translateX('view'),
        onClick: () => handlePreviewDocument?.(record),
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: translateX('edit'),
        onClick: () => handleEditDocument?.(record),
      },
      {
        key: 'archive',
        icon: <ContainerOutlined />,
        label: translateX('archive'),
        onClick: () => handleArchiveDocuments?.([record], actionOptions),
        disabled: !canModify,
      },
      {
        key: 'remove',
        icon: <RestOutlined />,
        label: translateX('remove'),
        onClick: () => handleRemoveDocuments?.([record], actionOptions),
        danger: true,
        disabled: !canModify,
      },
    ];

    return renderActionButtons(allButtons);
  }, []); // Empty deps - uses ref for latest values

  // Stable render function for ARCHIVE and RECYCLE folders
  const renderArchiveRecycleActions = useCallback((_, record) => {
    const { handleRestoreDocuments, handleRemoveDocuments, refresh, folder } = handlersRef.current;
    const isRecycle = folder === 'recycle';
    const actionOptions = { onSuccess: refresh };

    // Build all action buttons in priority order
    const allButtons = [
      {
        key: 'restore',
        icon: <PiLifebuoy />,
        label: translateX('restore'),
        onClick: () => handleRestoreDocuments?.([record], actionOptions),
      },
      {
        key: 'remove',
        icon: <RestOutlined />,
        label: isRecycle ? translateX('permanently_remove') : translateX('remove'),
        onClick: () => handleRemoveDocuments?.([record], actionOptions),
        danger: isRecycle,
      },
    ];

    return renderActionButtons(allButtons);
  }, []); // Empty deps - uses ref for latest values

  // Expanded row render - shows goods receipt items in a nested table
  const expandedRowRender = useCallback(record => {
    const items = record?.items || [];

    if (items.length === 0) {
      return (
        <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
          {translateX('no_items_added')}
        </div>
      );
    }

    // Columns for the items subtable (subset: no discount, CEST, IBPT, tax columns)
    const itemColumns = [
      itemLineNumberColumn(),
      itemSkuColumn({ title: translateX('code') }),
      itemDescriptionColumn(),
      itemUnitColumn(),
      itemQuantityColumn(),
      itemUnitPriceColumn(),
      itemTotalColumn(),
      itemNcmColumn({ className: undefined }),
      itemCfopColumn(),
    ];

    // Add unique keys to items that don't have _id
    const itemsWithKeys = items.map((item, idx) => ({
      ...item,
      _rowKey: item?._id || item?.product?._id || `item-${record._id}-${idx}`,
    }));

    return (
      <Table
        columns={itemColumns}
        dataSource={itemsWithKeys}
        rowKey="_rowKey"
        pagination={false}
        size="small"
        bordered={false}
        showHeader={true}
        scroll={{ x: 'max-content' }}
        className="nested-items-table"
      />
    );
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Reset expanded rows (close all subtables) when sort/search/filter/pagination changes
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [sortKey, searchKey, filterKey, paginationKey]);

  // Register disabled actions for float button bar
  // Disables archive/remove when ANY selected document is not in draft status
  useEffect(() => {
    registerFloatButtonDisabledActions?.((selectedRows, actionKey) => {
      if (!['archive', 'remove'].includes(actionKey)) return false;
      return selectedRows.some(row => row?.status !== 'draft');
    });

    return () => {
      registerFloatButtonDisabledActions?.(null);
    };
  }, [registerFloatButtonDisabledActions]);

  useEffect(() => {
    buildTableColumns([
      // Line number
      buildNumberLineColumn(),

      // TIPO - Document type (NFe, NFCe) — plain Tag render (no InvoiceTypesTag)
      documentTypeColumn(buildColumnProps),

      // STATUS - draft/confirmed/cancelled with Tag
      statusColumn(buildColumnProps),

      // NOTA - documentNumber-documentSerie, always shown (no SEFAZ requirement)
      documentNumberColumn(buildColumnProps, {
        requireSefaz: false,
        searchPlaceholder: 'document_number_or_access_key',
      }),

      // PEDIDO - purchaseOrderNumber (array)
      orderNumberColumn(buildColumnProps, { dataIndex: 'purchaseOrderNumber' }),

      // NATUREZA - raw text from supplier NF (not translated)
      operationNatureColumn(buildColumnProps, { rawText: true }),

      // OPERAÇÃO - national/import with Tag
      operationTypeColumn(buildColumnProps),

      // EMISSOR - issuer name + CNPJ subtitle
      issuerColumn(buildColumnProps),

      // EMISSÃO - issueDate, always shown (no SEFAZ requirement)
      issueDateColumn(buildColumnProps, { requireSefaz: false }),

      // ITENS - items count
      itemsCountColumn(buildColumnProps),

      // TOTAL - totals.documentAmount
      totalAmountColumn(buildColumnProps),

      // Created at
      buildCreatedAtColumn(),

      // Updated at
      buildUpdatedAtColumn(),

      // Icons Document Info
      buildDocumentInfoIconsColumn({}),

      // Actions - Custom render based on folder
      buildActionsButtonColumn({
        view: true,
        customRender: ['archive', 'recycle'].includes(folder)
          ? renderArchiveRecycleActions
          : renderDocumentsActions,
      }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    filterKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
    folder,
    // Note: renderDocumentsActions and renderArchiveRecycleActions are NOT included
    // as dependencies to avoid infinite loops. They use refs/closures for latest values.
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <TableFeatures />

      <Table
        key={tableKey}
        rowKey="_id"
        bordered
        columns={availableTableColumns}
        dataSource={data || []}
        loading={loading}
        onChange={handleTableChanges}
        pagination={pagination}
        rowSelection={rowSelection}
        scroll={{ x: 'max-content' }}
        size="middle"
        className="with-checkbox"
        components={tableComponents}
        expandable={
          !isEditingTable
            ? {
                expandedRowRender,
                rowExpandable: record => (record?.items?.length || 0) > 0,
                expandedRowKeys,
                onExpandedRowsChange: setExpandedRowKeys,
              }
            : undefined
        }
        locale={{
          emptyText: (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <FileTextOutlined style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 16 }} />
              <div>{translateX('no_records_found')}</div>
            </div>
          ),
        }}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default GoodsReceiptInit;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Renders action buttons with responsive behavior
 * - Desktop: up to 4 visible buttons (if more, show 3 + MORE)
 * - Mobile: 1 visible if only 1 button, otherwise all in MORE
 * - MORE button disabled if all menu items are disabled
 * @param {Array} buttons - Array of button configs { key, icon, label, onClick, disabled?, danger?, loading? }
 * @returns {JSX.Element}
 */
function renderActionButtons(buttons) {
  if (!buttons || buttons.length === 0) return null;

  const isMobile = isMobileView();

  // Mobile: 1 visible if only 1, otherwise all in MORE
  if (isMobile) {
    if (buttons.length === 1) {
      const btn = buttons[0];
      return (
        <Tooltip title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
            loading={btn.loading}
          />
        </Tooltip>
      );
    }

    // Multiple buttons on mobile: all in MORE dropdown
    const allDisabled = buttons.every(btn => btn.disabled);
    const menuItems = buttons.map(btn => ({
      key: btn.key,
      icon: btn.icon,
      label: btn.label,
      danger: btn.danger,
      disabled: btn.disabled,
      onClick: btn.onClick,
    }));

    return (
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allDisabled} />
        </Tooltip>
      </Dropdown>
    );
  }

  // Desktop: up to 4 visible (if more, show 3 + MORE)
  if (buttons.length <= 4) {
    return (
      <Space size={4}>
        {buttons.map(btn => (
          <Tooltip key={btn.key} title={btn.label}>
            <Button
              size="small"
              type="text"
              icon={btn.icon}
              onClick={btn.onClick}
              disabled={btn.disabled}
              danger={btn.danger}
              loading={btn.loading}
            />
          </Tooltip>
        ))}
      </Space>
    );
  }

  // More than 4 buttons: show first 3 + MORE
  const visibleButtons = buttons.slice(0, 3);
  const menuButtons = buttons.slice(3);
  const allMenuDisabled = menuButtons.every(btn => btn.disabled);

  const menuItems = menuButtons.map(btn => ({
    key: btn.key,
    icon: btn.icon,
    label: btn.label,
    danger: btn.danger,
    disabled: btn.disabled,
    onClick: btn.onClick,
  }));

  return (
    <Space size={4}>
      {visibleButtons.map(btn => (
        <Tooltip key={btn.key} title={btn.label}>
          <Button
            size="small"
            type="text"
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            danger={btn.danger}
            loading={btn.loading}
          />
        </Tooltip>
      ))}
      <Dropdown menu={{ items: menuItems }} trigger={['click']} disabled={allMenuDisabled}>
        <Tooltip title={translateX('more')}>
          <Button size="small" type="text" icon={<MoreOutlined />} disabled={allMenuDisabled} />
        </Tooltip>
      </Dropdown>
    </Space>
  );
}
